// Client-side cache for MCAP's already-aggregated tab summaries. Deliberately
// sessionStorage, not localStorage — clears the moment the tab closes rather
// than lingering on a shared admin machine. 30-minute TTL matches the backend
// Redis cache's own short-lived-but-not-instant tradeoff: long enough that
// switching between MCAP's 5 tabs doesn't refetch on every click, short
// enough that "live" still means something.
//
// Only ever store what an MCAP API response already returned — aggregated
// KPI numbers and chart series. Never write raw per-student rows here.
// Five minutes, not thirty. Thirty was chosen so tab-switching never refetched,
// but it also meant an administrator could sit on figures half an hour stale
// with nothing on screen saying so — and after a deploy or a data change that
// reads as "the release didn't work". Five still spares the API a burst of
// requests as someone moves across the tabs.
const TTL_MS = 5 * 60 * 1000;

// The build stamp. A deploy changes it, which orphans every entry written by
// the previous build — so new API fields and corrected figures appear the
// moment the new bundle loads, instead of up to a TTL later. CRA injects the
// hash at build time; the fallback only matters in local dev, where a reload
// already picks up changes.
const BUILD_ID = process.env.REACT_APP_BUILD_ID || process.env.NODE_ENV || "dev";
const PREFIX = `mcap:${BUILD_ID}:`;

// When the entry was written, so the UI can say how old the number is rather
// than leaving the reader to guess.
export function getCachedAt(key) {
  try {
    const raw = sessionStorage.getItem(PREFIX + key);
    if (!raw) return null;
    const { cachedAt } = JSON.parse(raw);
    return cachedAt ?? null;
  } catch {
    return null;
  }
}

// Drops every MCAP entry for this build — what the refresh control calls before
// refetching.
export function clearCache() {
  try {
    Object.keys(sessionStorage)
      .filter((key) => key.startsWith("mcap:"))
      .forEach((key) => sessionStorage.removeItem(key));
  } catch {
    // Storage unavailable: nothing was cached either, so nothing to clear.
  }
}

export function getCached(key) {
  try {
    const raw = sessionStorage.getItem(PREFIX + key);
    if (!raw) return null;
    const { value, expiresAt } = JSON.parse(raw);
    if (Date.now() > expiresAt) {
      sessionStorage.removeItem(PREFIX + key);
      return null;
    }
    return value;
  } catch {
    return null;
  }
}

export function setCached(key, value) {
  try {
    sessionStorage.setItem(
      PREFIX + key,
      JSON.stringify({ value, cachedAt: Date.now(), expiresAt: Date.now() + TTL_MS }),
    );
  } catch {
    // sessionStorage can throw (quota, private-mode Safari) — a cache write
    // failing is never worse than not caching, so swallow it.
  }
}

// Every filter that changes an MCAP query's result must be folded into its
// cache key, same discipline as the backend's Redis keys — a coarser key
// silently serves a different filter combination's data.
export function mcapCacheKey(prefix, filters = {}) {
  return `${prefix}:${filters.startDate || ""}:${filters.endDate || ""}:${filters.schoolId || ""}:${filters.sectionId || ""}`;
}
